"use client";

import React, { useEffect, useRef, useState } from "react";
import { CircleCheck, Paintbrush, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const interiorFeatures = ["Meticulous dustless sanding and preparation", "Premium, low-VOC paints for a healthier home", "Crisp, clean lines and flawless finishes", "Expert color consultation available"];
const exteriorFeatures = ["Specialised salt-air and weather-resistant coatings", "Thorough pressure washing and surface sealing", "Protection against fading, peeling, and moisture", "Over 30 years of local coastal experience"];
export default function InteriorExteriorPainting() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: row1Ref,
    isVisible: isRow1Visible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: row2Ref,
    isVisible: isRow2Visible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="interior-exterior-painting" className="relative bg-background py-24 overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full overflow-hidden leading-none opacity-[0.03] pointer-events-none">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-[150px]">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-primary"></path>
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {}
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-4xl font-bold text-primary tracking-tight sm:text-5xl mb-6">
            The Perfect Finish, Inside & Out
          </h2>
          <p className="text-lg text-muted-foreground">
            With over 30 years of experience on the Northern Central Coast, we deliver high-end residential painting services tailored to our unique coastal environment.
          </p>
        </div>

        {}
        <div ref={row1Ref} className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24 transition-all duration-1000 delay-100 ${isRow1Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="relative h-[400px] sm:h-[500px] rounded-2xl overflow-hidden shadow-xl group">
            <div className="absolute inset-0 bg-primary/10 group-hover:bg-transparent transition-colors duration-500 z-10"></div>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1588340/img/interior_painting_professional_finish.png" alt="High-end interior painting with crisp lines" className="object-cover w-full h-full transform group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute bottom-6 left-6 bg-background/95 backdrop-blur-sm p-4 rounded-xl shadow-lg z-20 flex items-center gap-3">
              <div className="bg-accent/20 p-2 rounded-full text-accent-foreground">
                <Paintbrush className="w-6 h-6" />
              </div>
              <div>
                <p className="font-semibold text-foreground text-sm">Precision Detail</p>
                <p className="text-xs text-muted-foreground">Flawless execution</p>
              </div>
            </div>
          </div>

          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/5 text-primary text-sm font-medium mb-6">
              <span className="w-2 h-2 rounded-full bg-accent"></span>
              Interior Services
            </div>
            <h3 className="text-3xl font-bold text-foreground mb-6">
              Transform Your Living Spaces
            </h3>
            <p className="text-muted-foreground mb-8 text-lg leading-relaxed">
              We believe the secret to a perfect interior finish lies in the preparation. Our team takes the time to properly prep every surface, ensuring crisp, clean lines and a flawless, long-lasting result that breathes new life into your home.
            </p>
            <ul className="space-y-4 mb-10">
              {interiorFeatures.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                  <CircleCheck className="w-6 h-6 text-accent shrink-0 mt-0.5" />
                  <span className="text-foreground">{feature}</span>
                </li>)}
            </ul>
            <Button asChild size="lg" className="rounded-full px-8">
              <Link to="/ContactPage">Get an Interior Quote</Link>
            </Button>
          </div>
        </div>

        {}
        <div ref={row2Ref} className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center transition-all duration-1000 delay-100 ${isRow2Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/5 text-primary text-sm font-medium mb-6">
              <span className="w-2 h-2 rounded-full bg-accent"></span>
              Exterior Services
            </div>
            <h3 className="text-3xl font-bold text-foreground mb-6">
              Durable Coastal Protection
            </h3>
            <p className="text-muted-foreground mb-8 text-lg leading-relaxed">
              Living on the coast means your home faces harsh salt air, intense sun, and unpredictable weather. We exclusively use premium, weather-resistant paints designed to protect your property while maintaining a beautiful, high-end aesthetic.
            </p>
            <ul className="space-y-4 mb-10">
              {exteriorFeatures.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                  <CircleCheck className="w-6 h-6 text-accent shrink-0 mt-0.5" />
                  <span className="text-foreground">{feature}</span>
                </li>)}
            </ul>
            <Button asChild size="lg" variant="outline" className="rounded-full px-8 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
              <Link to="/ContactPage">Request an Exterior Assessment</Link>
            </Button>
          </div>

          <div className="order-1 lg:order-2 relative h-[400px] sm:h-[500px] rounded-2xl overflow-hidden shadow-xl group">
            <div className="absolute inset-0 bg-primary/10 group-hover:bg-transparent transition-colors duration-500 z-10"></div>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1588340/img/exterior_painting_coastal_residence.png" alt="Durable exterior painting on a coastal home" className="object-cover w-full h-full transform group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute bottom-6 right-6 bg-background/95 backdrop-blur-sm p-4 rounded-xl shadow-lg z-20 flex items-center gap-3">
              <div className="bg-accent/20 p-2 rounded-full text-accent-foreground">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div>
                <p className="font-semibold text-foreground text-sm">Weather Resistant</p>
                <p className="text-xs text-muted-foreground">Salt-air protection</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}